import React, { useState, useEffect } from 'react';
import {
    Box, Button, Dialog, DialogContent, Typography, Select, MenuItem, IconButton, TextField,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import { TargetGroupFields, TARGET_GROUP_SCOPES, validateTargetGroup } from './RequestLearningProgramsWizard.comp';

/**
 * The partner-suggested program exchange, school side.
 *
 * School Workspace deck slides 47-50 (SW-P1A-MyPrograms-PartnerSuggested) and
 * Network Workspace deck slides 65-68 (SNW-P1A-MyPrograms-PartnerSuggested).
 * The two decks are identical.
 *
 *   47 / 65   Review & adjust — the suggestion, three editable fields
 *   48 / 66   Advanced program options
 *   49 / 67   Confirmation after Accept or Request changes
 *   50 / 68   View — preview the program
 *
 * WHY AREA IS LOCKED. Slide 47 marks Area "locked" while class/group, session
 * count and session length are dropdowns. The partner suggested this program
 * because the assessment pointed at that COS area; letting the school change the
 * area would make it a different program and break the link back to the
 * evidence. Duration and audience are the school's to set — the subject is not.
 *
 * ADVANCED PROGRAM OPTIONS (slide 48) is not shown yet. Ian (17 Sep): partners
 * should receive those answers, but that flow is not in the mockups and is to be
 * discussed once the additional options are streamlined. Offering a form whose
 * answers go nowhere would mislead the school, so the button returns with the flow.
 */

const NAVY = '#052F44';
const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const BORDER = 'var(--brand-border, #E5E7EB)';

/*
 * The ranges the school's own request wizard allows and the API accepts
 * (1-10 sessions, 30-60 minutes). The old list offered "15 sessions", which the
 * API silently dropped. Slide 47's "5 sessions" / "45 minutes" is the starting
 * value when the suggestion carries none.
 */
const SESSION_COUNTS = Array.from({ length: 10 }, (_, i) => `${i + 1} session${i === 0 ? '' : 's'}`);
const DEFAULT_SESSIONS = '5 sessions';

/** The whole-school option every group picker starts with. */
export const WHOLE_SCHOOL_OPTION = { key: 'whole', label: 'Whole school', grade_level: '', class_name: '' };

/** The option key for a stored grade / class pair. */
export const groupKeyFor = (grade_level, class_name) =>
    (class_name ? `class:${class_name}` : grade_level ? `grade:${grade_level}` : 'whole');

const FieldRow = ({ label, children }) => (
    <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '175px 1fr' },
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        borderBottom: `1px solid ${BORDER}`,
    }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY }}>{label}</Typography>
        {children}
    </Box>
);

/** A field shown but not editable — the read-only Review (disable reselection). */
const LockedValue = ({ value }) => (
    <TextField size="small" value={value} disabled fullWidth
        sx={{
            '& .MuiInputBase-root': { backgroundColor: 'var(--brand-surface-subtle, #F3F4F6)', fontSize: '14px' },
            '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'var(--brand-text, #111827)' },
        }} />
);

/** Slides 47 / 65 — "Learning Program Suggested for You". */
export const ProgramSuggestionReviewDialog = ({
    open,
    onClose,
    onDecline,
    onRequestChanges,
    onAccept,
    area = '',
    // The current group as an option key (groupKeyFor), and every option.
    group = 'whole',
    groupOptions = [],
    sessions,
    sessionLength,
    sending = false,
    /*
     * Read-only: the same layout with every field locked and only Close (SW-46 /
     * SNW-64 legend, Ian 2 Oct 2026). A row the school REQUESTED shows its own
     * request this way ("disable reselection"), and so does a suggestion that
     * was already answered. groupLabel is the row's group as text.
     */
    readOnly = false,
    requestedBySchool = false,
    groupLabel = '',
    /*
     * Cohort as the partner's "Edit cohort" picks it (PW slide 20): Group type,
     * then Grade / Class — the request wizard's TargetGroupFields. Used when
     * grades/classes are given; the row's own grade_level/class_name seed it.
     */
    initial = null,
    grades = null,
    classes = [],
    // The label beside the locked group box. "School" when the box holds a
    // school's name rather than a class or group (LA-SNW-13Oct-03).
    groupFieldLabel = 'Class / group',
}) => {
    const startSessions = sessions || DEFAULT_SESSIONS;
    const cohortMode = Array.isArray(grades);
    const { SCOPE_SCHOOL, SCOPE_GRADE, SCOPE_CLASS } = TARGET_GROUP_SCOPES;
    const startDetail = () => ({
        groupType: initial?.class_name ? SCOPE_CLASS.label : initial?.grade_level ? SCOPE_GRADE.label : SCOPE_SCHOOL.label,
        groupValue: initial?.class_name || initial?.grade_level || '',
        groupInstituteCode: initial?.institute_code || '',
        groupValues: [], groupInstituteCodes: [],
    });
    const [detail, setDetail] = useState(startDetail);
    const [chosenGroup, setChosenGroup] = useState(group);
    const [chosenSessions, setChosenSessions] = useState(startSessions);

    /*
     * Declining and asking for changes both need the school to say WHY.
     *
     * Not decoration: respondToSuggestion refuses either answer without text,
     * because the partner reads it verbatim — in the Growth Opportunities row
     * and in the mail they are sent. With no field here, both buttons sent a
     * request the API was always going to reject.
     *
     * Revealed rather than always shown, so the default view stays the three
     * fields and four buttons of slide 47.
     */
    const [reasonFor, setReasonFor] = useState(null);   // null | 'changes' | 'decline'
    const [reason, setReason] = useState('');

    // Re-seed when a different row is opened; without this the dialog keeps the
    // previous suggestion's selections.
    useEffect(() => {
        if (open) {
            setChosenGroup(group);
            setDetail(startDetail());
            setChosenSessions(startSessions);
            setReasonFor(null);
            setReason('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, group, sessions, sessionLength]);

    // Whole school first, then whatever the school actually has. The current
    // group is always present, so the Select never holds a value it cannot show.
    const groups = (() => {
        const list = [WHOLE_SCHOOL_OPTION, ...groupOptions.filter((g) => g.key !== 'whole')];
        return list;
    })();
    const pickedGroup = groups.find((g) => g.key === chosenGroup) || WHOLE_SCHOOL_OPTION;

    const wholeSchool = detail.groupType === SCOPE_SCHOOL.label;
    const cohortChoices = detail.groupType === SCOPE_CLASS.label ? classes
        : detail.groupType === SCOPE_GRADE.label ? (grades || [])
        : [];
    const cohortErrors = validateTargetGroup(detail, wholeSchool);
    const cohortValid = !cohortMode || (Boolean(detail.groupType) && !cohortErrors.groupType && !cohortErrors.groupValue);

    const payload = () => (cohortMode
        ? {
            group: wholeSchool ? 'Whole school' : detail.groupValue,
            grade_level: detail.groupType === SCOPE_GRADE.label ? detail.groupValue : '',
            class_name: detail.groupType === SCOPE_CLASS.label ? detail.groupValue : '',
            sessions: chosenSessions,
        }
        : {
            group: pickedGroup.label,
            grade_level: pickedGroup.grade_level,
            class_name: pickedGroup.class_name,
            sessions: chosenSessions,
        });

    /** True once the revealed box holds something worth sending. */
    const reasonReady = reason.trim().length > 0;

    /*
     * The fields are LOCKED on Review — they show what the partner suggested,
     * and Accept accepts exactly that. Only Request changes unlocks them, so
     * every change goes back to the partner for confirmation instead of slipping
     * through on Accept.
     */
    const editing = !readOnly && reasonFor === 'changes';
    const resetFields = () => {
        setChosenGroup(group);
        setDetail(startDetail());
        setChosenSessions(startSessions);
    };

    /*
     * First press reveals the box; the second sends. Splitting it this way keeps
     * the resting dialog exactly as slide 47 draws it and still guarantees the
     * API never receives an answer it has to refuse.
     */
    const negative = (kind, send) => () => {
        if (reasonFor !== kind) { setReasonFor(kind); setReason(''); return; }
        // A decline needs its reason; for a change request it is optional.
        if (kind === 'decline' && !reasonReady) return;
        send();
    };

    /*
     * What the partner reads under "What they asked for". The school's words if
     * it wrote any; otherwise the group and sessions it chose, so the request
     * is never blank (the API and the partner's email both need a text).
     */
    const changesText = () => reason.trim()
        || `Class / group: ${payload().group} · Number of sessions: ${payload().sessions}`;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY }}>
                        {requestedBySchool ? 'Your Learning Program Request' : 'Learning Program Suggested for You'}
                    </Typography>
                    <IconButton size="small" onClick={onClose} aria-label="Close">
                        <CloseIcon style={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                <Typography sx={{ fontSize: '0.88rem', color: MUTED, mb: 2.5, lineHeight: 1.6 }}>
                    {requestedBySchool ? (
                        <>You requested this program to strengthen <strong>{area}</strong>.</>
                    ) : readOnly ? (
                        <>Based on your COS Children Assessment, your DQ Partner suggested this program to strengthen <strong>{area}</strong>.</>
                    ) : (
                        <>Based on your COS Children Assessment, your DQ Partner suggests this program
                        to strengthen <strong>{area}</strong>. Use <strong>Request changes</strong> to
                        adjust the group or sessions &mdash; changes go back for confirmation.</>
                    )}
                </Typography>

                {/*
                  * Slide 47 draws Area as a FILLED, DISABLED INPUT with a "locked" pill
                  * beside it — not as plain text. That matters: it sits in the same
                  * column as the three dropdowns below and has to read as a field of the
                  * same form that simply cannot be edited. Plain text reads as a caption
                  * and loses the "this is part of the form, but fixed" meaning.
                  */}
                <FieldRow label="Area">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            value={area}
                            disabled
                            sx={{
                                flexGrow: 1,
                                minWidth: 200,
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'var(--brand-surface-subtle, #F3F4F6)',
                                    fontSize: '14px',
                                },
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: 'var(--brand-text, #111827)',
                                },
                            }}
                        />
                        <Typography sx={{
                            fontSize: '11.5px', fontWeight: 600,
                            color: MUTED,
                            backgroundColor: 'var(--brand-surface-subtle, #F3F4F6)',
                            border: `1px solid ${BORDER}`, borderRadius: '6px',
                            px: 1.25, py: '5px',
                        }}>
                            locked
                        </Typography>
                    </Box>
                </FieldRow>

                {/*
                  * Cohort mode: the partner's Edit cohort fields at full width, as in
                  * that modal — two selects side by side do not fit the label column.
                  * minWidth 0 lets a long class name ("Grade 5 - Class A · School")
                  * truncate instead of widening the dialog.
                  */}
                {editing && cohortMode ? (
                <Box sx={{ py: 1.5, borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY, mb: 1 }}>Class / group</Typography>
                    <Box sx={{ minWidth: 0, '& > div': { mb: 0 }, '& > div > *': { minWidth: 0 } }}>
                        <TargetGroupFields
                            detail={detail}
                            onChange={setDetail}
                            groupChoices={cohortChoices}
                            isNetwork={false}
                            errors={detail.groupType ? cohortErrors : {}}
                        />
                    </Box>
                </Box>
                ) : (
                <FieldRow label={editing ? 'Class / group' : groupFieldLabel}>
                    {!editing ? <LockedValue value={groupLabel || pickedGroup.label} /> : (
                    <Select size="small" value={groups.some((g) => g.key === chosenGroup) ? chosenGroup : 'whole'}
                            onChange={(e) => setChosenGroup(e.target.value)}
                            sx={{ fontSize: '14px' }} fullWidth>
                        {groups.map((g) => <MenuItem key={g.key} value={g.key}>{g.label}</MenuItem>)}
                    </Select>
                    )}
                </FieldRow>
                )}

                <FieldRow label="Number of sessions">
                    {!editing ? <LockedValue value={sessions || '—'} /> : (
                    <Select size="small" value={chosenSessions} onChange={(e) => setChosenSessions(e.target.value)}
                            sx={{ fontSize: '14px' }} fullWidth>
                        {SESSION_COUNTS.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                    </Select>
                    )}
                </FieldRow>

                {/*
                  * No "Time per session" — hidden per Ian (17 Sep): users no longer
                  * choose a session length; LearnCreator fixes the content per session.
                  */}

                {/*
                  * Slide 47/65 puts FOUR buttons in one row:
                  *   Decline · Advanced program options · Request changes · Accept
                  *
                  * "Advanced program options" is a real outlined button there, not a
                  * link, and the slide note says clicking it goes to the next slide.
                  */}

                {/*
                  * The reason box, shown only once a negative answer is chosen.
                  * Its label names the reader, because the partner is the one who
                  * acts on it.
                  */}
                {reasonFor && (
                    <Box sx={{ mt: 2.5 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY, mb: 1 }}>
                            {reasonFor === 'decline'
                                ? 'Why are you declining?'
                                : 'What would you like changed? (optional)'}
                        </Typography>
                        <TextField
                            autoFocus
                            multiline
                            minRows={3}
                            fullWidth
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={reasonFor === 'decline'
                                ? 'e.g. no budget for this term'
                                : 'e.g. Grade 5 only, and 30-minute sessions'}
                            inputProps={{ maxLength: 500 }}
                            sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                        />
                        <Typography sx={{ fontSize: '12px', color: MUTED, mt: 0.75 }}>
                            Your partner sees this exactly as written.
                        </Typography>
                    </Box>
                )}

                {/*
                  * With the reason box open, the only choices are to send that answer
                  * or go back. Accept beside "Send decline" offered the opposite answer
                  * in the same breath.
                  */}
                {readOnly ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button variant="outlined" onClick={onClose}
                            sx={{ textTransform: 'none', fontWeight: 700, color: MUTED, borderColor: BORDER }}>
                        Close
                    </Button>
                </Box>
                ) : reasonFor ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap', mt: 3 }}>
                    <Button variant="outlined"
                            disabled={sending}
                            onClick={() => { setReasonFor(null); setReason(''); resetFields(); }}
                            sx={{ textTransform: 'none', fontWeight: 700, color: MUTED, borderColor: BORDER }}>
                        Back
                    </Button>
                    <Button variant="contained"
                            disabled={sending || (reasonFor === 'decline' && !reasonReady) || (editing && !cohortValid)}
                            onClick={reasonFor === 'decline'
                                ? negative('decline', () => onDecline && onDecline({ ...payload(), decline_reason: reason.trim() }))
                                : negative('changes', () => onRequestChanges && onRequestChanges({ ...payload(), requested_changes: changesText() }))}
                            sx={{
                                textTransform: 'none', fontWeight: 700, backgroundColor: ORANGE, color: '#fff',
                                '&:hover': { backgroundColor: '#e55a00' },
                            }}>
                        {reasonFor === 'decline' ? 'Send decline' : 'Send request'}
                    </Button>
                </Box>
                ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap', mt: 3 }}>
                    <Button variant="outlined"
                            disabled={sending}
                            onClick={negative('decline', () => onDecline && onDecline({ ...payload(), decline_reason: reason.trim() }))}
                            sx={{ textTransform: 'none', fontWeight: 700, color: MUTED, borderColor: BORDER }}>
                        Decline
                    </Button>
                    <Button variant="outlined"
                            disabled={sending}
                            onClick={negative('changes', () => onRequestChanges && onRequestChanges({ ...payload(), requested_changes: reason.trim() }))}
                            sx={{ textTransform: 'none', fontWeight: 700 }}>
                        Request changes
                    </Button>
                    {/* Fields are locked here, so this accepts the suggestion as sent. */}
                    <Button variant="contained"
                            disabled={sending}
                            onClick={() => onAccept && onAccept(payload())}
                            sx={{
                                textTransform: 'none', fontWeight: 700, backgroundColor: ORANGE, color: '#fff',
                                '&:hover': { backgroundColor: '#e55a00' },
                            }}>
                        Accept
                    </Button>
                </Box>
                )}
            </DialogContent>

        </Dialog>
    );
};

/**
 * Request Changes — the school asks its partner to change a program the partner
 * is working on (accepted, being built, or built but not yet published), Ian /
 * Usman, 2 Oct 2026. Only the partner publishes.
 *
 * The cohort uses the SAME fields as the partner's "Edit cohort" (PW slide 20):
 * the request wizard's TargetGroupFields — Group type, then Grade / Class —
 * plus the number of sessions and a free-text message. It all reaches the
 * partner as one message on their Pipeline row; nothing on the request itself
 * is overwritten, because the partner may already be building it.
 */
export const RequestProgramChangesDialog = ({
    open, onClose, onSend, programName = '', sending = false,
    initial = null, sessions: currentSessions, grades = [], classes = [], isNetwork = false,
}) => {
    const { SCOPE_SCHOOL, SCOPE_GRADE, SCOPE_CLASS } = TARGET_GROUP_SCOPES;
    const startGroup = () => ({
        groupType: initial?.class_name ? SCOPE_CLASS.label : initial?.grade_level ? SCOPE_GRADE.label : SCOPE_SCHOOL.label,
        groupValue: initial?.class_name || initial?.grade_level || '',
        groupInstituteCode: initial?.institute_code || '',
        groupValues: [], groupInstituteCodes: [],
    });
    const [detail, setDetail] = useState(startGroup);
    const [chosenSessions, setChosenSessions] = useState('');
    const [text, setText] = useState('');

    useEffect(() => {
        if (!open) return;
        setDetail(startGroup());
        setChosenSessions(currentSessions ? `${currentSessions} session${Number(currentSessions) === 1 ? '' : 's'}` : '');
        setText('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const wholeSchool = detail.groupType === SCOPE_SCHOOL.label;
    const choices = detail.groupType === SCOPE_CLASS.label ? classes
        : detail.groupType === SCOPE_GRADE.label ? grades
        : [];
    const errors = validateTargetGroup(detail, wholeSchool);
    const groupValid = Boolean(detail.groupType) && !errors.groupType && !errors.groupValue;
    const groupLabel = wholeSchool ? 'Whole school' : detail.groupValue;
    const startLabel = (() => { const g = startGroup(); return g.groupType === SCOPE_SCHOOL.label ? 'Whole school' : g.groupValue; })();
    const startSessions = currentSessions ? `${currentSessions} session${Number(currentSessions) === 1 ? '' : 's'}` : '';

    // One message for the partner: what changed, then the school's own words.
    const changed = [
        groupLabel !== startLabel ? `Cohort: ${groupLabel}` : null,
        chosenSessions && chosenSessions !== startSessions ? `Number of sessions: ${chosenSessions}` : null,
        text.trim() || null,
    ].filter(Boolean);
    /*
     * The reason is OPTIONAL. With nothing typed and nothing changed, the
     * message is the scope as it stands, so the partner still has something to
     * read and the row can show that a request is waiting.
     */
    const lines = changed.length ? changed : [
        `Cohort: ${groupLabel || 'Whole school'}`,
        chosenSessions ? `Number of sessions: ${chosenSessions}` : null,
    ].filter(Boolean);
    const ready = groupValid;

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY }}>
                        Request changes
                    </Typography>
                    <IconButton size="small" onClick={onClose} disabled={sending} aria-label="Close">
                        <CloseIcon style={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
                <Typography sx={{ fontSize: '0.88rem', color: MUTED, mb: 2, lineHeight: 1.6 }}>
                    Tell your DQ Partner what you would like changed in <strong>{programName}</strong>.
                    They will update the program before publishing it.
                </Typography>

                <Box sx={{ minWidth: 0, '& > div > *': { minWidth: 0 } }}>
                    <TargetGroupFields
                        detail={detail}
                        onChange={setDetail}
                        groupChoices={choices}
                        isNetwork={isNetwork}
                        errors={detail.groupType ? errors : {}}
                    />
                </Box>

                <FieldRow label="Number of sessions">
                    <Select size="small" value={chosenSessions} displayEmpty onChange={(e) => setChosenSessions(e.target.value)}
                            sx={{ fontSize: '14px' }} fullWidth>
                        {!chosenSessions && <MenuItem value="" disabled>Select</MenuItem>}
                        {SESSION_COUNTS.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                    </Select>
                </FieldRow>

                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY, mt: 2, mb: 1 }}>
                    What else would you like changed? (optional)
                </Typography>
                <TextField
                    multiline
                    minRows={3}
                    fullWidth
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="e.g. Add more examples for Grade 5, and shorten session 2"
                    inputProps={{ maxLength: 800 }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                />
                <Typography sx={{ fontSize: '12px', color: MUTED, mt: 0.75 }}>
                    Your partner sees this exactly as written.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
                    <Button variant="outlined" onClick={onClose} disabled={sending}
                            sx={{ textTransform: 'none', fontWeight: 700, color: MUTED, borderColor: BORDER }}>
                        Back
                    </Button>
                    <Button variant="contained" disabled={sending || !ready}
                            onClick={() => onSend && onSend(lines.join('\n'))}
                            sx={{
                                textTransform: 'none', fontWeight: 700, backgroundColor: ORANGE, color: '#fff',
                                '&:hover': { backgroundColor: '#e55a00' },
                            }}>
                        {sending ? 'Sending…' : 'Send request'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

/** Slides 49 / 67 — after Accept or Request changes. */
export const ProgramRequestSentDialog = ({ open, onClose }) => (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, mb: 1.5 }}>
                Awesome!
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: NAVY, mb: 1.5 }}>
                Your request has been successfully sent to your DQ Partner.
            </Typography>
            <Typography sx={{ fontSize: '0.86rem', color: MUTED, lineHeight: 1.7, mb: 3 }}>
                They&rsquo;ll begin creating your custom learning program and notify you as soon as
                it&rsquo;s ready for review. In the meantime, sit tight&mdash;we&rsquo;ll let you know
                the moment your program is available in your Powered by DQ Workspace.
            </Typography>
            <Button variant="contained" onClick={onClose}
                    sx={{
                        textTransform: 'none', fontWeight: 700, minWidth: 120,
                        backgroundColor: ORANGE, color: '#fff',
                        '&:hover': { backgroundColor: '#e55a00' },
                    }}>
                Ok
            </Button>
        </DialogContent>
    </Dialog>
);

/**
 * Slides 50 / 68 — the "View" button.
 *
 * The slide states the scope as a restriction: "program outline, content, and
 * assessments only - no dashboard preview since they already have it". So this
 * shows the three sections it names and nothing else; the omission of a
 * dashboard is deliberate, not unfinished.
 */
export const ProgramPreviewDialog = ({ open, onClose, programName = '' }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY }}>
                    {programName || 'Learning program'}
                </Typography>
                <IconButton size="small" onClick={onClose} aria-label="Close">
                    <CloseIcon style={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            {['Program outline', 'Content', 'Assessments'].map((section) => (
                <Box key={section} sx={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: '10px',
                    p: 2,
                    mb: 1.5,
                }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY, mb: 0.5 }}>
                        {section}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: MUTED }}>
                        Available once your Partner has built the program.
                    </Typography>
                </Box>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: MUTED }}>Close</Button>
            </Box>
        </DialogContent>
    </Dialog>
);
